import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaBan,
  FaDownload,
  FaStar,
  FaEdit,
  FaHistory,
  FaGlobe,
  FaBirthdayCake,
  FaUserTie,
  FaWallet,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaBuilding,
  FaUser,
  FaIdCard,
  FaDollarSign,
  FaTrash
} from 'react-icons/fa';
import { IoCloseOutline } from 'react-icons/io5';
import { SINGAPORE_BANKS } from '../../utils/constants';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Providers.css';
import ConfirmModal from '../Common/ConfirmModal';
import SuspendModal from '../Common/SuspendModal';
import EditPostModal from '../Common/EditPostModal';
import ViewPostModal from '../Common/ViewPostModal';
import { FaEye } from 'react-icons/fa';

const ProviderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [payments, setPayments] = useState([]);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalModal, setApprovalModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [suspendModal, setSuspendModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [reactivateModal, setReactivateModal] = useState(false);
  const [reason, setReason] = useState('');
  const [commissionOverride, setCommissionOverride] = useState('0');

  const getFullUrl = (url) => {
    if (!url) return null;
    // If it's already an absolute URL (http, https, blob) or a local file path, don't prepend
    if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
      return url;
    }

    // Explicitly handle file:/// URIs - these are invalid for a web browser in this context
    if (url.startsWith('file:///')) {
      return null; // Return null so we don't try to fetch it
    }

    const baseUrl = ENDPOINTS.AUTH.LOGIN.replace('/auth/login', '').replace('/api', '');
    return `${baseUrl}/${url.startsWith('/') ? url.slice(1) : url}`;
  };

  const fetchProvider = useCallback(async () => {
    setLoading(true);
    try {
      try {
        const response = await api.get(`${ENDPOINTS.PROVIDERS.base}/${id}`);
        setProvider(response.data);

        if (response.data.commissionRate !== undefined && response.data.commissionRate !== null) {
          setCommissionOverride(response.data.commissionRate.toString());
        } else {
          // Fetch system default if provider has no rate set
          try {
            const settingsRes = await api.get(ENDPOINTS.SETTINGS.commission.default);
            setCommissionOverride(settingsRes.data.value || '0');
          } catch (err) {
            setCommissionOverride('0');
          }
        }
      } catch (err) {
        console.error('Failed to fetch provider main data:', err);
        setProvider(null); // This will trigger "Provider not found" view which is correct if ID is wrong
        throw err;
      }

      try {
        const payRes = await api.get(ENDPOINTS.PAYMENTS.base, {
          params: { providerId: id, limit: 100 }
        });
        const paymentsData = payRes.data?.data || payRes.data || [];
        console.log(`Fetched ${paymentsData.length} payments for provider ${id}`);
        setPayments(paymentsData);
      } catch (err) {
        console.warn('Failed to fetch payments for provider:', err);
        setPayments([]);
      }

      try {
        const postRes = await api.get(ENDPOINTS.COMMUNITY.USER_POSTS(id));
        setPosts(postRes.data?.posts || postRes.data || []);
      } catch (err) {
        console.warn('Failed to fetch provider community contributions:', err);
      }
    } catch (error) {
      console.error('Failed complete fetch:', error);
      // Main error toast only if main provider fetch fails
      if (!provider) toast.error('Failed to load provider profile');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const [editingPost, setEditingPost] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [viewingPost, setViewingPost] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchProvider();
    }
  }, [id, fetchProvider]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get(ENDPOINTS.SERVICES.CATEGORIES);
        setCategories(response.data || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  const handleApprove = async () => {
    try {
      await api.post(`${ENDPOINTS.PROVIDERS.base}/${id}/approve`, {
        commissionRate: parseFloat(commissionOverride)
      });
      toast.success('Provider approved successfully!');
      setApprovalModal(false);
      fetchProvider();
    } catch (error) {
      console.error('Failed to approve provider:', error);
      toast.error('Failed to approve provider: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }
    try {
      await api.post(`${ENDPOINTS.PROVIDERS.base}/${id}/reject`, { reason });
      toast.success('Provider rejected successfully');
      setRejectModal(false);
      fetchProvider();
    } catch (error) {
      console.error('Failed to reject provider:', error);
      toast.error('Failed to reject provider: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSuspend = async (suspendReason) => {
    try {
      await api.post(`${ENDPOINTS.PROVIDERS.base}/${id}/suspend`, { reason: suspendReason });
      toast.success('Provider suspended successfully');
      setSuspendModal(false);
      fetchProvider();
    } catch (error) {
      console.error('Failed to suspend provider:', error);
      toast.error('Failed to suspend provider: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleReactivate = async () => {
    try {
      await api.post(`${ENDPOINTS.PROVIDERS.base}/${id}/reactivate`);
      toast.success('Provider reactivated successfully');
      setReactivateModal(false);
      fetchProvider();
    } catch (error) {
      console.error('Failed to reactivate provider:', error);
      toast.error('Failed to reactivate provider: ' + (error.response?.data?.message || error.message));
    }
  };


  const handleEditPost = (post) => {
    setEditingPost(post);
    setIsEditModalOpen(true);
  };

  const handleViewPost = (post) => {
    setViewingPost(post);
    setIsViewModalOpen(true);
  };

  const handleDeletePost = (postId) => {
    setPostToDelete(postId);
  };

  const confirmPostDelete = async () => {
    if (!postToDelete) return;
    try {
      setProcessing(true);
      await api.delete(ENDPOINTS.COMMUNITY.DELETE_POST(postToDelete));
      setPosts(posts.filter(p => p.id !== postToDelete));
      toast.success("Post deleted successfully");
    } catch (err) {
      console.error("Failed to delete post:", err);
      toast.error("Failed to delete post");
    } finally {
      setProcessing(false);
      setPostToDelete(null);
    }
  };

  const handleUpdateCommission = async () => {
    try {
      await api.patch(`${ENDPOINTS.PROVIDERS.base}/${id}/commission`, {
        commissionRate: parseFloat(commissionOverride)
      });
      toast.success('Commission rate updated successfully');
      fetchProvider();
    } catch (error) {
      console.error('Failed to update commission:', error);
      toast.error('Failed to update commission');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading provider details...</p>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="error-container">
        <h3>Provider not found</h3>
        <button className="btn btn-primary" onClick={() => navigate('/providers')}>
          Back to Providers
        </button>
      </div>
    );
  }

  return (
    <div className="provider-details-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/providers')}>
            <FaArrowLeft /> Back
          </button>
          <div>
            <h1>Providers</h1>
            <p className="page-subtitle">Review and manage provider onboarding requests</p>
          </div>
        </div>
      </div>

      {/* Provider Header Card */}
      <div className="provider-header-card">
        <div className="provider-header-content-v2">
          <div className="avatar-wrapper">
            <img
              src={getFullUrl(provider.avatar) || `https://ui-avatars.com/api/?name=${encodeURIComponent(provider.firstName || '')}+${encodeURIComponent(provider.lastName || '')}&background=4834d4&color=fff&bold=true&size=200`}
              alt="avatar"
              onError={(e) => { 
                e.target.onerror = null; 
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(provider.firstName || 'P')}&background=4834d4&color=fff&bold=true&size=200`; 
              }}
            />
          </div>
          <div className="provider-header-left">
            <h2>{provider.firstName} {provider.lastName}</h2>
            <p className="provider-category">{provider.businessName || 'Independent Provider'}</p>
            <p className="provider-submitted">Joined on: {new Date(provider.createdAt).toLocaleDateString()}</p>
          </div>
        </div>

        <div className="provider-header-right">
          <div className="status-container">
            <span className={`status-badge ${provider.status?.toLowerCase()}`}>
              {provider.status}
            </span>
            {provider.status === 'approved' && provider.approvedAt && (
              <p className="approved-timestamp">
                Approved on: {new Date(provider.approvedAt).toLocaleDateString()} {new Date(provider.approvedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
          <button className="btn-close" onClick={() => navigate('/providers')}>
            ×
          </button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="provider-details-grid">
        {/* Left Column */}
        <div className="provider-left-column">
          {/* Provider Profile Overview */}
          <div className="content-card">
            <h3 className="card-title">Provider Profile Overview</h3>
            <div className="profile-info-grid">
              <div className="profile-item-v2">
                <FaBuilding className="p-icon" />
                <div className="p-content">
                  <label>Business Name</label>
                  <span>
                    {provider.businessName && provider.businessName !== ""
                      ? provider.businessName
                      : 'Independent Provider'}
                  </span>
                </div>
              </div>

              <div className="profile-item-v2">
                <FaEnvelope className="p-icon" />
                <div className="p-content">
                  <label>Contact Email</label>
                  <span>{provider.email}</span>
                </div>
              </div>

              <div className="profile-item-v2">
                <FaPhoneAlt className="p-icon" />
                <div className="p-content">
                  <label>Phone</label>
                  <span>{provider.phone || 'N/A'}</span>
                </div>
              </div>

              <div className="profile-item-v2">
                <FaIdCard className="p-icon" />
                <div className="p-content">
                  <label>UEN (Company Registration)</label>
                  <span>
                    {provider.uen && provider.uen !== ""
                      ? provider.uen
                      : 'Not Registered'}
                  </span>
                </div>
              </div>

              <div className="profile-item-v2">
                <FaUserTie className="p-icon" />
                <div className="p-content">
                  <label>Contact Person</label>
                  <span>{provider.contactPerson || 'N/A'}</span>
                </div>
              </div>

              <div className="profile-item-v2">
                <FaMapMarkerAlt className="p-icon" />
                <div className="p-content">
                  <label>Address</label>
                  <span>
                    {provider.address || 'N/A'}
                    {provider.unitNumber && ` #${provider.unitNumber}`}
                    {provider.zipCode && ` (${provider.zipCode})`}
                    {provider.city && `, ${provider.city}`}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Financial & Performance Summary */}
          <div className="content-card performance-card">
            <h3 className="card-title">Financial & Performance Summary</h3>
            <div className="stats-grid-v2">
              <div className="stat-box-v2 gold">
                <FaStar className="s-icon" />
                <div className="s-data">
                  <span className="s-label">Rating</span>
                  <span className="s-value">{Number(provider.rating || 0).toFixed(1)} / 5.0</span>
                </div>
              </div>

              <div className="stat-box-v2 green">
                <FaHistory className="s-icon" />
                <div className="s-data">
                  <span className="s-label">Total Bookings</span>
                  <span className="s-value">{provider.totalBookings || 0}</span>
                </div>
              </div>

              <div className="stat-box-v2 blue">
                <FaDollarSign className="s-icon" />
                <div className="s-data">
                  <span className="s-label">Total Earnings</span>
                  <span className="s-value">${Number(provider.totalEarnings || 0).toFixed(2)}</span>
                </div>
              </div>

              <div className="stat-box-v2 indigo">
                <FaWallet className="s-icon" />
                <div className="s-data">
                  <span className="s-label">Available Balance</span>
                  <span className="s-value">${Number(provider.availableBalance || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Experience & Personal Details */}
          <div className="content-card">
            <h3 className="card-title">Experience & Personal Details</h3>
            <div className="profile-info-grid">
              {(provider.serviceDetails?.yearsOfExperience || provider.yearsOfExperience) && (
                <div className="profile-item-v2">
                  <FaUser className="p-icon" />
                  <div className="p-content">
                    <label>Years of Experience</label>
                    <span>{provider.serviceDetails?.yearsOfExperience || provider.yearsOfExperience} Years</span>
                  </div>
                </div>
              )}

              {(provider.serviceDetails?.languagesSpoken || provider.languagesSpoken) && (
                <div className="profile-item-v2">
                  <FaGlobe className="p-icon" />
                  <div className="p-content">
                    <label>Languages Spoken</label>
                    <span>{provider.serviceDetails?.languagesSpoken || provider.languagesSpoken}</span>
                  </div>
                </div>
              )}

              {(provider.serviceDetails?.nationality || provider.nationality) && (
                <div className="profile-item-v2">
                  <FaGlobe className="p-icon" />
                  <div className="p-content">
                    <label>Nationality</label>
                    <span>{provider.serviceDetails?.nationality || provider.nationality}</span>
                  </div>
                </div>
              )}

              {/* {(provider.serviceDetails?.age || provider.age) && (
                <div className="profile-item-v2">
                  <FaBirthdayCake className="p-icon" />
                  <div className="p-content">
                    <label>Age</label>
                    <span>{provider.serviceDetails?.age || provider.age}</span>
                  </div>
                </div>
              )} */}

              <div className="profile-item-v2">
                <FaDollarSign className="p-icon" />
                <div className="p-content">
                  <label>Commission Rate</label>
                  <span>{provider.commissionRate || 0}%</span>
                </div>
              </div>

              <div className="profile-item-v2">
                <FaCalendarAlt className="p-icon" />
                <div className="p-content">
                  <label>Referral Code</label>
                  <span>{provider.referralCode || 'GENERATING...'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Document Verification */}
          <div className="content-card">
            <h3 className="card-title">Document Verification</h3>

            {[
              //{ label: 'ID / NRIC / FIN', field: 'idDocumentUrl', statusField: 'idVerificationStatus' },
              { label: 'Professional Certifications', field: 'certificationDocumentUrl', statusField: 'certificationStatus' },
              { label: 'Insurance Documents', field: 'insuranceDocumentUrl', statusField: 'insuranceStatus' }
            ].map((doc, idx) => {
              const url = provider[doc.field];
              let status = provider[doc.statusField];

              // Logic: If provider is approved, all their documents are considered verified
              if (provider.status === 'approved' && (!status || status.toLowerCase() === 'pending')) {
                status = 'verified';
              }

              // Logic: If provider is rejected, all their pending documents are considered failed
              if (provider.status === 'rejected' && (!status || status.toLowerCase() === 'pending')) {
                status = 'failed';
              }

              const fullUrl = getFullUrl(url);

              return (
                <div key={idx} className="document-section">
                  <h4>{doc.label}</h4>
                  <div className="document-item">
                    <div className="doc-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '10px' }}>
                      <span className={`doc-status-badge ${(status || '').toLowerCase()}`}>
                        Status: {status || 'Pending'}
                      </span>
                      {url && (
                        <div className="doc-actions" style={{ display: 'flex', gap: '10px' }}>
                          {fullUrl ? (
                            <>
                              <button className="btn-download" onClick={() => window.open(fullUrl)}>
                                Download
                              </button>
                              <button
                                className="btn-view"
                                style={{
                                  padding: '5px 12px',
                                  backgroundColor: '#F0F9FF',
                                  color: '#0369A1',
                                  border: '1px solid #BAE6FD',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85em',
                                  fontWeight: '600'
                                }}
                                onClick={() => window.open(fullUrl)}
                              >
                                View in Browser
                              </button>
                            </>
                          ) : (
                            <span style={{ fontSize: '0.8em', color: '#E11D48', fontWeight: 'bold' }}>Cannot Open Local File</span>
                          )}
                        </div>
                      )}
                    </div>

                    {url ? (
                      <div className="document-preview-container">
                        {url.toLowerCase().endsWith('.pdf') ? (
                          <div
                            className="pdf-preview-placeholder"
                            onClick={() => fullUrl && window.open(fullUrl)}
                            style={{
                              width: '100%',
                              height: '150px',
                              backgroundColor: '#F1F5F9',
                              border: '2px dashed #CBD5E1',
                              borderRadius: '8px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              cursor: fullUrl ? 'pointer' : 'default',
                              color: '#64748B'
                            }}
                          >
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                              <line x1="16" y1="13" x2="8" y2="13"></line>
                              <line x1="16" y1="17" x2="8" y2="17"></line>
                              <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                            <span style={{ marginTop: '10px', fontWeight: '500' }}>PDF Document</span>
                            <span style={{ fontSize: '0.8em', color: '#94A3B8' }}>{fullUrl ? 'Click to open in new tab' : 'Invalid File Path from Mobile'}</span>
                          </div>
                        ) : (url.startsWith('file:///') || !fullUrl) ? (
                          <div
                            className="invalid-path-placeholder"
                            style={{
                              width: '100%',
                              height: '100px',
                              backgroundColor: '#FFF1F2',
                              border: '1px solid #FECACA',
                              borderRadius: '8px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              color: '#B91C1C',
                              padding: '15px',
                              textAlign: 'center',
                              fontSize: '0.9em'
                            }}
                          >
                            <strong>⚠ Invalid Upload:</strong> This file was never uploaded to the server (saved as local path: {url}). Please ask provider to re-upload.
                          </div>
                        ) : (
                          <div className="document-preview" onClick={() => window.open(fullUrl)}>
                            <img
                              src={fullUrl}
                              alt={`${doc.label} Preview`}
                              className="document-image"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/400x300?text=Sync+Error:+Ask+Provider+to+Re-Upload';
                              }}
                              style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #E2E8F0' }}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <p style={{ color: '#94A3B8', fontStyle: 'italic', padding: '10px' }}>No document uploaded.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Banking Information */}
          <div className="content-card">
            <h3 className="card-title">Banking Information</h3>
            <div className="profile-info">
              <div className="profile-row">
                <span className="profile-label">Account Holder:</span>
                <span className="profile-value">{provider.bankAccountHolderName || 'N/A'}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Bank Name:</span>
                <span className="profile-value">
                  {(() => {
                    const isBank1 = SINGAPORE_BANKS.includes(provider.bankRoutingNumber);
                    const isBank2 = SINGAPORE_BANKS.includes(provider.bankName);

                    if (isBank1) return provider.bankRoutingNumber;
                    if (isBank2) return provider.bankName;

                    // Fallback to existing manual detection
                    return provider.bankName || provider.bankRoutingNumber || (
                      <span style={{ color: '#EF4444', fontWeight: '500' }}>N/A (Please update)</span>
                    );
                  })()}
                </span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Account Number:</span>
                <span className="profile-value">{provider.bankAccountNumber || 'N/A'}</span>
              </div>
              {/* <div className="profile-row">
                <span className="profile-label">Routing / Branch Code:</span>
                <span className="profile-value">
                  {(() => {
                    const isBank1 = SINGAPORE_BANKS.includes(provider.bankRoutingNumber);
                    const isBank2 = SINGAPORE_BANKS.includes(provider.bankName);

                    if (isBank1) return provider.bankName || 'N/A';
                    if (isBank2) return provider.bankRoutingNumber || 'N/A';

                    // Default fallback
                    return provider.bankName?.length < provider.bankRoutingNumber?.length
                      ? provider.bankName
                      : provider.bankRoutingNumber || 'N/A';
                  })()}
                </span>
              </div> */}
            </div>
          </div>

          <div className="content-card">
            <h3 className="card-title">Background Check Status</h3>
            <div className="bg-check-info">
              {provider.backgroundCheckProvider && (
                <div className="bg-check-row">
                  <span className="bg-check-label">Provider:</span>
                  <span className="bg-check-value">{provider.backgroundCheckProvider}</span>
                </div>
              )}
              <div className="bg-check-row">
                <span className="bg-check-label">Status:</span>
                <span className={`bg-check-badge ${(provider.status === 'approved' && (!provider.backgroundCheckStatus || provider.backgroundCheckStatus.toLowerCase() === 'pending')) ? 'verified' : (provider.status === 'rejected' && (!provider.backgroundCheckStatus || provider.backgroundCheckStatus.toLowerCase() === 'pending')) ? 'failed' : (provider.backgroundCheckStatus || '').toLowerCase()}`}>
                  {(provider.status === 'approved' && (!provider.backgroundCheckStatus || provider.backgroundCheckStatus.toLowerCase() === 'pending')) ? 'Verified' : (provider.status === 'rejected' && (!provider.backgroundCheckStatus || provider.backgroundCheckStatus.toLowerCase() === 'pending')) ? 'Failed' : (provider.backgroundCheckStatus || 'Pending')}
                </span>
              </div>
              {provider.backgroundCheckCompletedAt && (
                <div className="bg-check-row">
                  <span className="bg-check-label">Completed on:</span>
                  <span className="bg-check-value">
                    {new Date(provider.backgroundCheckCompletedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Provider Videos */}
          <div className="content-card">
            <h3 className="card-title">Provider Videos</h3>
            {provider.videos && provider.videos.length > 0 ? (
              <div className="videos-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                {provider.videos.map((video) => (
                  <div key={video.id} className="video-card" style={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#F8FAFC' }}>
                    <video
                      src={getFullUrl(video.videoUrl)}
                      controls
                      className="provider-detail-video"
                      style={{ width: '100%', height: '200px', backgroundColor: '#000' }}
                    >
                      Your browser does not support the video tag.
                    </video>
                    <div style={{ padding: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontWeight: 'bold', color: '#2C3E5C' }}>{video.category?.name || 'Uncategorized'}</span>
                        <span style={{ fontWeight: 'bold', color: '#7BA9E5', fontSize: '1.1em' }}>${video.price}</span>
                      </div>
                      {video.notes && <p style={{ fontSize: '0.9em', color: '#64748B', marginBottom: '15px' }}>{video.notes}</p>}
                      <button className="btn-download" onClick={() => window.open(getFullUrl(video.videoUrl))} style={{ width: '100%' }}>
                        Download Video
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#7B8FA3', fontStyle: 'italic' }}>No videos uploaded.</p>
            )}
          </div>

          {/* Specialized Service Details */}
          {provider.serviceDetails && (
            <div className="content-card">
              <h3 className="card-title">Specialized Service Details</h3>
              <div className="profile-info">
                {provider.serviceDetails.languagesSpoken && (
                  <div className="profile-row">
                    <span className="profile-label">Languages:</span>
                    <span className="profile-value">{provider.serviceDetails.languagesSpoken}</span>
                  </div>
                )}
                {provider.serviceDetails.yearsOfExperience !== undefined && (
                  <div className="profile-row">
                    <span className="profile-label">Experience:</span>
                    <span className="profile-value">{provider.serviceDetails.yearsOfExperience} Years</span>
                  </div>
                )}
                {/*  {provider.serviceDetails.age !== undefined && provider.serviceDetails.age !== "" && (
                  <div className="profile-row">
                    <span className="profile-label">Age:</span>
                    <span className="profile-value">{provider.serviceDetails.age} Years</span>
                  </div>
                )} */}
                {provider.serviceDetails.nationality && (
                  <div className="profile-row">
                    <span className="profile-label">Nationality:</span>
                    <span className="profile-value">{provider.serviceDetails.nationality}</span>
                  </div>
                )}
                {provider.serviceDetails.availableFrom && (
                  <div className="profile-row">
                    <span className="profile-label">Available From:</span>
                    <span className="profile-value">{new Date(provider.serviceDetails.availableFrom).toLocaleDateString()}</span>
                  </div>
                )}
                {provider.serviceDetails.certificationOrQualification && (
                  <div className="profile-row">
                    <span className="profile-label">Certification:</span>
                    <span className="profile-value">{provider.serviceDetails.certificationOrQualification}</span>
                  </div>
                )}
                {provider.serviceDetails.monthlyRate != null && provider.serviceDetails.monthlyRate !== "" && (
                  <div className="profile-row">
                    <span className="profile-label">Monthly Rate (30D):</span>
                    <span className="profile-value" style={{ fontWeight: 'bold', color: '#10B981' }}>${Number(provider.serviceDetails.monthlyRate).toLocaleString()}</span>
                  </div>
                )}
                {provider.serviceDetails.extraChargesInfo && (
                  <div className="profile-row">
                    <span className="profile-label">Extra Charges:</span>
                    <span className="profile-value" style={{ color: '#F43F5E', fontSize: '0.9em' }}>{provider.serviceDetails.extraChargesInfo}</span>
                  </div>
                )}

                {/* Cloud Arrays */}
                {provider.serviceDetails.ageGroupsComfortableWith?.length > 0 && (
                  <div className="profile-row">
                    <span className="profile-label">Age Groups:</span>
                    <div className="services-tags">
                      {provider.serviceDetails.ageGroupsComfortableWith.map((tag, idx) => (
                        <span key={idx} className="service-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                {provider.serviceDetails.confinementServicesProvided?.length > 0 && (
                  <div className="profile-row">
                    <span className="profile-label">Nanny Services:</span>
                    <div className="services-tags">
                      {provider.serviceDetails.confinementServicesProvided.map((tag, idx) => (
                        <span key={idx} className="service-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                {provider.serviceDetails.cuisinesExperiencedIn?.length > 0 && (
                  <div className="profile-row">
                    <span className="profile-label">Cuisines:</span>
                    <div className="services-tags">
                      {provider.serviceDetails.cuisinesExperiencedIn.map((tag, idx) => (
                        <span key={idx} className="service-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                {provider.serviceDetails.consultationTypesOffered?.length > 0 && (
                  <div className="profile-row">
                    <span className="profile-label">Consultation:</span>
                    <div className="services-tags">
                      {provider.serviceDetails.consultationTypesOffered.map((tag, idx) => (
                        <span key={idx} className="service-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                {provider.serviceDetails.preferredServiceLocation?.length > 0 && (
                  <div className="profile-row">
                    <span className="profile-label">Service Locations:</span>
                    <div className="services-tags">
                      {provider.serviceDetails.preferredServiceLocation.map((tag, idx) => (
                        <span key={idx} className="service-tag secondary">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                {provider.serviceDetails.roomPreference?.length > 0 && (
                  <div className="profile-row">
                    <span className="profile-label">Room Preference:</span>
                    <div className="services-tags">
                      {provider.serviceDetails.roomPreference.map((tag, idx) => (
                        <span key={idx} className="service-tag info">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                {provider.serviceDetails.serviceAreas?.length > 0 && (
                  <div className="profile-row">
                    <span className="profile-label">Service Areas:</span>
                    <div className="services-tags">
                      {provider.serviceDetails.serviceAreas.map((area, idx) => (
                        <span key={idx} className="service-tag secondary" style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>
                          {area.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Toggles */}
                <div className="profile-row-toggles" style={{ marginTop: '10px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  {provider.serviceDetails.availableForUrgentBooking && <span className="service-tag success">Urgent Booking</span>}
                  {provider.serviceDetails.availableForUrgentConsultation && <span className="service-tag success">Urgent Consult</span>}
                  {provider.serviceDetails.availableOnWeekends && <span className="service-tag success">Weekend Available</span>}
                  {provider.serviceDetails.comfortableWithPets && <span className="service-tag success">Pet Friendly</span>}
                </div>
              </div>
            </div>
          )}

          {/* Services Offered */}
          <div className="content-card">
            <h3 className="card-title">Services Offered</h3>
            <div className="services-info">
              <div className="services-row">
                <span className="services-label">Categories:</span>
                <div className="services-tags">
                  {provider.services && provider.services.length > 0 ? (
                    // Get unique categories
                    [...new Set(provider.services.map(s => s.category?.name || 'Caregiver'))].map((catName, idx) => (
                      <span key={idx} className="service-tag info" style={{ fontWeight: '700', textTransform: 'uppercase' }}>
                        {catName}
                      </span>
                    ))
                  ) : (
                    <span className="service-tag info" style={{ fontWeight: '700', textTransform: 'uppercase' }}>
                      {provider.category?.name || categories.find(c => c.id === Number(provider.categoryId))?.name || (provider.categoryId === 8 ? 'Confinement Nanny' : 'Caregiver')}
                    </span>
                  )}
                </div>
              </div>
              <div className="services-row">
                <span className="services-label">Individual Services:</span>
                <div className="services-tags">
                  {provider.services && provider.services.length > 0 ? (
                    provider.services.map((service, idx) => (
                      <span key={idx} className="service-tag">
                        {service.name || service}
                      </span>
                    ))
                  ) : (
                    <>
                      {provider.serviceDetails?.confinementServicesProvided?.length > 0 &&
                        provider.serviceDetails.confinementServicesProvided.map((service, idx) => (
                          <span key={`conf-${idx}`} className="service-tag">
                            {service}
                          </span>
                        ))
                      }
                      {provider.serviceDetails?.consultationTypesOffered?.length > 0 &&
                        provider.serviceDetails.consultationTypesOffered.map((service, idx) => (
                          <span key={`cons-${idx}`} className="service-tag">
                            {service}
                          </span>
                        ))
                      }
                      {(!provider.serviceDetails?.confinementServicesProvided?.length &&
                        !provider.serviceDetails?.consultationTypesOffered?.length) && (
                        <span>No services assigned</span>
                      )}
                    </>
                  )}
                </div>
              </div>
              {provider.specialization && (
                <div className="services-row">
                  <span className="services-label">Specialization:</span>
                  <span className="services-value">{provider.specialization}</span>
                </div>
              )}
              <div className="services-row">
                <span className="services-label">Total Completed Bookings:</span>
                <span className="services-value">{provider.totalBookings}</span>
              </div>
            </div>
          </div>

          {/* Commission Settings */}
          <div className="content-card">
            <h3 className="card-title">Commission Settings</h3>
            <div className="commission-settings">
              <div className="commission-row">
                <label>Commission Percentage:</label>
                <div className="commission-input-group">
                  <input
                    type="number"
                    value={commissionOverride}
                    onChange={(e) => setCommissionOverride(e.target.value)}
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>
              <button className="btn btn-secondary" onClick={handleUpdateCommission}>Update Commission</button>
            </div>
          </div>

          {/* Administrative Status */}
          {/* <div className="content-card">
            <h3 className="card-title">Loyalty & Rewards</h3>
            <div className="profile-info">
              <div className="profile-row">
                <span className="profile-label">Loyalty Points:</span>
                <span className="profile-value" style={{ fontWeight: 'bold', color: '#7BA9E5' }}>{provider.loyaltyPoints || 0} pts</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Current Tier:</span>
                <span className={`service-tag tier-${(provider.loyaltyTier || 'bronze').toLowerCase()}`} style={{ textTransform: 'capitalize' }}>
                  {provider.loyaltyTier || 'Bronze'}
                </span>
              </div>
            </div>
          </div> */}

          {/* Risk Flags & Internal Notes */}
          <div className="content-card" style={provider.isHighRisk ? { borderLeft: '5px solid #EF4444' } : {}}>
            <h3 className="card-title">Internal Notes</h3>
            <div className="risk-view-section">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', color: provider.isHighRisk ? '#EF4444' : '#64748B' }}>
                <span style={{ fontSize: '1.2em', marginRight: '10px' }}>
                  {provider.isHighRisk ? '🚩' : '✅'}
                </span>
                <span style={{ fontWeight: 'bold' }}>
                  {provider.isHighRisk ? 'High Risk Account' : 'Account in Good Standing'}
                </span>
              </div>

              <div className="notes-box" style={{
                backgroundColor: '#F8FAFC',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
                minHeight: '80px'
              }}>
                <span style={{ display: 'block', fontSize: '0.85em', color: '#64748B', marginBottom: '5px', fontWeight: 'bold' }}>Internal Admin Notes:</span>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#334155' }}>
                  {provider.internalNotes || 'No internal notes recorded.'}
                </p>
              </div>
            </div>
          </div>

          {/* PAYMENTS TABLE */}
          <div className="content-card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 className="card-title" style={{ margin: 0 }}>Payment History</h3>
              <span style={{ backgroundColor: '#DBEAFE', color: '#1E40AF', padding: '4px 12px', borderRadius: '12px', fontSize: '0.85em', fontWeight: 'bold' }}>
                {payments.length || 0}
              </span>
            </div>

            {!payments.length ? (
              <p style={{ color: '#7B8FA3', fontStyle: 'italic', padding: '10px 0' }}>No payments found for this provider.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E2E8F0', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px', color: '#64748B' }}>Booking ID</th>
                      <th style={{ padding: '12px 8px', color: '#64748B' }}>Customer</th>
                      <th style={{ padding: '12px 8px', color: '#64748B', textAlign: 'center' }}>Amount</th>
                      <th style={{ padding: '12px 8px', color: '#64748B', textAlign: 'center' }}>Date</th>
                      <th style={{ padding: '12px 8px', color: '#64748B', textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                        <td style={{ padding: '12px 8px', fontWeight: 'bold', color: '#0F172A' }}>BID{p.booking_id || 'N/A'}</td>
                        <td style={{ padding: '12px 8px', color: '#334155' }}>{p.booking?.customer?.firstName} {p.booking?.customer?.lastName}</td>
                        <td style={{ padding: '12px 8px', fontWeight: 'bold', color: '#0F172A', textAlign: 'center' }}>${Number(p.amount || 0).toFixed(2)}</td>
                        <td style={{ padding: '12px 8px', color: '#334155', textAlign: 'center' }}>{new Date(p.createdAt).toLocaleDateString()}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.85em',
                            fontWeight: 'bold',
                            backgroundColor: (p.status || '').toUpperCase() === 'COMPLETED' ? '#DCFCE7' : '#FEF9C3',
                            color: (p.status || '').toUpperCase() === 'COMPLETED' ? '#166534' : '#854D0E',
                            display: 'inline-block'
                          }}>
                            {(p.status || '').toUpperCase() === 'COMPLETED' ? 'Completed' : p.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Approval Actions */}
        <div className="provider-right-column">
          <div className="content-card approval-card">
            <h3 className="card-title">Approval Actions</h3>
            <div className="approval-buttons">
              {provider.status === 'pending' && (
                <>
                  <button
                    className="btn btn-success btn-large"
                    onClick={() => setApprovalModal(true)}
                  >
                    <FaCheckCircle /> Approve Provider
                  </button>
                  <button
                    className="btn btn-danger btn-large"
                    onClick={() => setRejectModal(true)}
                  >
                    <FaBan /> Reject Provider
                  </button>
                </>
              )}

              {provider.status === 'approved' && (
                <button
                  className="btn btn-warning btn-large"
                  onClick={() => setSuspendModal(true)}
                >
                  <FaBan /> Suspend Provider
                </button>
              )}

              {provider.status === 'suspended' && (
                <button
                  className="btn btn-success btn-large"
                  onClick={() => setReactivateModal(true)}
                >
                  <FaHistory /> Reactivate Provider
                </button>
              )}

              {provider.status === 'rejected' && (
                <p className="status-note">This provider has been rejected.</p>
              )}
            </div>
          </div>

          {/* Services Offered List */}
          <div className="content-card services-list-card mt-4">
            <h3 className="card-title">Services List</h3>
            <div className="services-list-compact">
              {provider.services && provider.services.length > 0 ? (
                provider.services.map((service) => (
                  <div key={service.id} className="service-item-row">
                    <div className="service-item-main">
                      <div className="service-name-text">{service.name}</div>
                      <div className="service-category-tag">{service.category?.name || 'General'}</div>
                    </div>
                    <div className="service-item-details text-end">
                      <div className="service-price-text">${service.basePrice}</div>
                      <div className="service-type-label" style={{ textTransform: 'capitalize' }}>{service.pricingType}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-services-placeholder text-center p-4">
                  <p className="text-muted mb-0">No services assigned to this provider.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* COMMUNITY ACTIVITY */}
      <div className="content-card modern community-section" style={{ marginTop: '24px' }}>
        <div className="card-header">
          <h3 className="card-title">Community Contribution</h3>
          <span className="badge tier">{posts.length || 0} Contributions</span>
        </div>
        {!posts.length ? (
          <div className="empty-state-small" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            This provider hasn't shared any community posts yet.
          </div>
        ) : (
          <div className="posts-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '24px',
            padding: '24px'
          }}>
            {posts.map((post) => (
              <div key={post.id} className="post-item-card" style={{
                position: 'relative',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                overflow: 'hidden',
                background: 'white',
                transition: 'transform 0.2s ease',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10, display: 'flex', gap: '8px' }}>
                  <button 
                    className="btn-view-post"
                    onClick={() => handleViewPost(post)}
                    title="View Post"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(16, 185, 129, 0.9)', // Emerald color for View
                      color: 'white',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FaEye size={14} />
                  </button>
                  {/* <button 
                    className="btn-edit-post"
                    onClick={() => handleEditPost(post)}
                    title="Edit Post"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(59, 130, 246, 0.9)', // Blue color for Edit
                      color: 'white',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FaEdit size={14} />
                  </button> */}
                  <button 
                    className="btn-delete-post"
                    onClick={() => handleDeletePost(post.id)}
                    title="Delete Post"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(239, 68, 68, 0.9)',
                      color: 'white',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
                <div className="post-media-container" style={{ position: 'relative', height: '200px', backgroundColor: '#f1f5f9' }}>
                  {post.mediaUrls && post.mediaUrls.length > 0 ? (
                    <img
                      src={getFullUrl(post.mediaUrls[0])}
                      alt="Post"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Image+Unavailable'; }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>No Media Content</div>
                  )}
                </div>
                <div className="post-body" style={{ padding: '20px' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1.1rem', color: '#0f172a', fontWeight: '700' }}>{post.title || "Observation Note"}</h4>
                  <p style={{
                    margin: 0,
                    fontSize: '0.9rem',
                    color: '#475569',
                    lineHeight: '1.6',
                    display: '-webkit-box',
                    WebkitLineClamp: '3',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>{post.content}</p>
                  <div style={{
                    marginTop: '16px',
                    paddingTop: '12px',
                    borderTop: '1px solid #f1f5f9',
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: '#94a3b8',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    <span>❤️ {post.likesCount || 0} Likes</span>
                    <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {approvalModal && (
        <div className="modal-overlay" onClick={() => setApprovalModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Approve Provider</h3>
              <button className="modal-close" onClick={() => setApprovalModal(false)}>
                <IoCloseOutline />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to approve <strong>{provider.firstName} {provider.lastName}</strong>?</p>
              <p className="modal-note">This will allow them to start accepting bookings on the platform.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setApprovalModal(false)}>
                Cancel
              </button>
              <button className="btn btn-success" onClick={handleApprove}>
                Approve Provider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="modal-overlay" onClick={() => setRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Provider</h3>
              <button className="modal-close" onClick={() => setRejectModal(false)}>
                <IoCloseOutline />
              </button>
            </div>
            <div className="modal-body">
              <p>Please provide a reason for rejection:</p>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows="4"
                className="modal-textarea"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setRejectModal(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleReject}>
                Reject Provider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      <SuspendModal
        isOpen={suspendModal}
        onClose={() => setSuspendModal(false)}
        onConfirm={handleSuspend}
        memberName={`${provider.firstName} ${provider.lastName}`}
        title="Suspend Provider Account"
        message="Please provide a reason for suspending this provider. They will not be able to log in or accept new bookings while suspended."
        confirmText="Suspend Provider"
      />

      {/* Reactivate Modal */}
      {reactivateModal && (
        <div className="modal-overlay" onClick={() => setReactivateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reactivate Provider</h3>
              <button className="modal-close" onClick={() => setReactivateModal(false)}>
                <IoCloseOutline />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to reactivate <strong>{provider.firstName} {provider.lastName}</strong>?</p>
              <p className="modal-note">This will allow them to accept bookings again.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setReactivateModal(false)}>
                Cancel
              </button>
              <button className="btn btn-success" onClick={handleReactivate}>
                Reactivate Provider
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal 
        isOpen={!!postToDelete}
        onClose={() => setPostToDelete(null)}
        onConfirm={confirmPostDelete}
        title="Delete Community Post?"
        message="Are you sure you want to delete this community post? This action cannot be undone."
        confirmText="Delete Post"
        type="danger"
      />
      {/* Edit Post Modal */}
      <EditPostModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        post={editingPost}
        onSuccess={fetchProvider}
      />

      {/* View Post Modal */}
      <ViewPostModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        post={viewingPost}
      />
    </div>
  );
};

export default ProviderDetails;